import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	fetchEssayVersions,
	fetchVersionChatMessages,
	markEssayVersionSeen,
	patchEssayVersion,
	patchVersionChatReadForCounselor,
	postVersionChatMessage,
} from 'api/essays'
import { syncFeedbackThreadSeen } from 'shared/utils/counselorSeenSync'
import Button from 'shared/ui/Button/Button'
import FeedbackPanel from '../FeedbackPanel/FeedbackPanel'
import PriorityDrawerShell from '../PriorityDrawerShell/PriorityDrawerShell'
import EssayContent from '../EssayContent/EssayContent'
import {
	areFeedbackItemsEqual,
	mapMessagesToFeedbackItems,
	parseJson,
} from '../../utils/feedbackMapping'
import EssayVersionDropdown from 'pages/ApplicationsPage/components/ApplicationEssayReviewDrawer/EssayVersionDropdown'
import EssayListSidePanel from 'pages/ApplicationsPage/components/ApplicationEssayReviewDrawer/EssayListSidePanel'
import { formatEssayShortDate } from 'pages/ApplicationDetailsPage/components/Essay/utils/formatEssayDate'
import s from './EssayReviewDrawer.module.scss'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as MessagesIcon } from 'assets/icons/messages.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as ListIcon } from 'assets/icons/list.svg'

const FEEDBACK_POLL_MS = 15000

const EssayReviewDrawer = ({
	variant = 'dashboard',
	controlled = null,
	items,
	index,
	onClose,
	onNavigate,
	onChanged,
	onResolvePending,
}) => {
	const isApplication = variant === 'application'
	const item = items[index]
	const [version, setVersion] = useState(null)
	const [loading, setLoading] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)
	const [feedbackSending, setFeedbackSending] = useState(false)
	const [isEditing, setIsEditing] = useState(false)
	const [feedbackOpen, setFeedbackOpen] = useState(false)
	const [titleDraft, setTitleDraft] = useState('')
	const [saving, setSaving] = useState(false)
	const [feedbackItems, setFeedbackItems] = useState([])
	const [feedbackLoading, setFeedbackLoading] = useState(true)
	const [unreadStudentCount, setUnreadStudentCount] = useState(0)
	const resolvedVersion = isApplication ? controlled?.version : version
	const resolvedLoading = isApplication ? Boolean(controlled?.loading) : loading
	const resolvedFeedbackItems = useMemo(
		() => (isApplication ? controlled?.feedbackItems || [] : feedbackItems),
		[isApplication, controlled?.feedbackItems, feedbackItems],
	)
	const resolvedFeedbackLoading = isApplication
		? Boolean(controlled?.feedbackLoading)
		: feedbackLoading
	const resolvedUnread = isApplication
		? Number(controlled?.unreadStudentCount) || 0
		: unreadStudentCount
	const [activeFeedbackId, setActiveFeedbackId] = useState(null)
	const [selection, setSelection] = useState(null)
	const [highlightedFeedbackId, setHighlightedFeedbackId] = useState(null)
	const editorRef = useRef(null)
	const lastSavedRef = useRef('')
	const pendingFocusFeedbackIdRef = useRef(null)
	const knownFeedbackIdsRef = useRef(new Set())
	const itemRef = useRef(item)
	itemRef.current = item

	const loadFeedback = useCallback(
		async (nextVersion, { silent = false } = {}) => {
			const current = itemRef.current
			if (!current?.essayId || !current?.versionId) return
			if (!silent) setFeedbackLoading(true)
			try {
				const res = await fetchVersionChatMessages(
					current.essayId,
					current.versionId,
				)
				const messages = Array.isArray(res?.data?.messages)
					? res.data.messages
					: []
				const unread = messages.filter(
					m => m?.sender_type === 'student' && !m?.is_read_by_counselor,
				).length
				const nextItems = mapMessagesToFeedbackItems(
					messages,
					nextVersion?.content_text || '',
				)
				setUnreadStudentCount(unread)
				setFeedbackItems(prev =>
					areFeedbackItemsEqual(prev, nextItems) ? prev : nextItems,
				)
				if (!silent) {
					await patchVersionChatReadForCounselor(
						current.essayId,
						current.versionId,
					).catch(() => {})
					syncFeedbackThreadSeen({
						versionId: current.versionId,
						essayId: current.essayId,
						unreadMessageCount: unread,
					})
				}
			} catch (e) {
				console.error(e)
				if (!silent) {
					setFeedbackItems([])
					setUnreadStudentCount(0)
				}
			} finally {
				if (!silent) setFeedbackLoading(false)
			}
		},
		[],
	)

	const changeEdit = () => {
		const newV = !isEditing

		setIsEditing(newV)
		if (newV) {
			setFeedbackOpen(true)
		}
	}

	const loadVersion = useCallback(async () => {
		const current = itemRef.current
		if (!current?.essayId || !current?.versionId) return
		setLoading(true)
		setIsEditing(false)
		setActiveFeedbackId(null)
		setSelection(null)
		setHighlightedFeedbackId(null)
		setFeedbackLoading(true)
		setFeedbackItems([])
		setUnreadStudentCount(0)
		pendingFocusFeedbackIdRef.current = null
		knownFeedbackIdsRef.current = new Set()
		try {
			const res = await fetchEssayVersions(current.essayId, {
				includeDrafts: true,
			})
			const versions = Array.isArray(res?.data?.versions)
				? res.data.versions
				: []
			const next =
				versions.find(v => String(v.id) === String(current.versionId)) ||
				versions[0] ||
				null
			setVersion(next)
			const title = String(current.title || '').trim() || 'Essay'
			setTitleDraft(title)
			lastSavedRef.current = JSON.stringify({
				title,
				contentJson: parseJson(next?.content_json),
				contentText: next?.content_text || '',
			})
			await loadFeedback(next)
			markEssayVersionSeen(current.versionId, {
				anchorAt: current.createdAt || next?.created_at || undefined,
			}).catch(() => {})
		} catch (e) {
			console.error(e)
			setVersion(null)
			setFeedbackItems([])
		} finally {
			setLoading(false)
		}
	}, [loadFeedback])

	useEffect(() => {
		if (isApplication) return undefined
		if (!item?.essayId || !item?.versionId) return undefined
		loadVersion()
		return undefined
	}, [isApplication, item?.essayId, item?.versionId, loadVersion])

	useEffect(() => {
		if (isApplication || !feedbackOpen) return undefined
		if (!item?.essayId || !item?.versionId) return undefined
		const id = window.setInterval(() => {
			loadFeedback(version, { silent: true })
		}, FEEDBACK_POLL_MS)
		return () => window.clearInterval(id)
	}, [
		feedbackOpen,
		isApplication,
		item?.essayId,
		item?.versionId,
		loadFeedback,
		version,
	])

	// Application variant: title comes from essay thread (item.title), not version loadVersion.
	useEffect(() => {
		if (!isApplication) return
		const essayTitle = String(item?.title || '').trim() || 'Essay'
		setTitleDraft(essayTitle)
	}, [isApplication, item])

	useEffect(() => {
		if (!isApplication || !resolvedVersion || !item) return
		const essayTitle = String(item.title || '').trim() || 'Essay'
		lastSavedRef.current = JSON.stringify({
			title: essayTitle,
			contentJson: parseJson(resolvedVersion?.content_json),
			contentText: resolvedVersion?.content_text || '',
		})
	}, [isApplication, item, resolvedVersion])

	useEffect(() => {
		const editor = editorRef.current
		if (!editor) return undefined
		editor.clearActiveAnchor?.()
		editor.setAnchors?.([])
		const anchors = resolvedFeedbackItems
			.map(feedback => {
				if (
					feedback?.resolvedAnchor?.start != null &&
					feedback?.resolvedAnchor?.end != null
				) {
					return {
						id: feedback.id,
						from: feedback.resolvedAnchor.start,
						to: feedback.resolvedAnchor.end,
					}
				}
				return null
			})
			.filter(Boolean)
		if (!anchors.length) return undefined
		let attempts = 0
		const applyAnchors = () => {
			if (!editorRef.current?.isReady?.()) {
				if (attempts < 10) {
					attempts += 1
					window.setTimeout(applyAnchors, 50)
				}
				return
			}
			editorRef.current.setAnchors?.(anchors)
		}
		applyAnchors()
		return undefined
	}, [resolvedFeedbackItems, item?.versionId])

	const saveNow = useCallback(async () => {
		if (
			!isEditing ||
			!item?.essayId ||
			!item?.versionId ||
			!editorRef.current?.getSnapshot
		)
			return
		const snapshot = editorRef.current.getSnapshot()
		if (!snapshot) return
		const payload = {
			title: titleDraft,
			content_json: snapshot.contentJson,
			content_text: snapshot.contentHtml,
			word_count: snapshot.wordCount,
		}
		const signature = JSON.stringify({
			title: titleDraft,
			contentJson: payload.content_json,
			contentText: payload.content_text,
		})
		if (signature === lastSavedRef.current) return
		setSaving(true)
		try {
			await patchEssayVersion(item.essayId, item.versionId, payload)
			lastSavedRef.current = signature
			onChanged?.()
		} catch (e) {
			console.error(e)
		} finally {
			setSaving(false)
		}
	}, [isEditing, item?.essayId, item?.versionId, onChanged, titleDraft])

	useEffect(() => {
		if (!isEditing) return undefined
		const id = window.setInterval(saveNow, 1500)
		return () => {
			window.clearInterval(id)
			saveNow()
		}
	}, [isEditing, saveNow])

	const handleApprove = async () => {
		if (!item?.essayId || !item?.versionId || actionLoading) return
		setActionLoading(true)
		try {
			await saveNow()
			await patchEssayVersion(item.essayId, item.versionId, {
				status: 'approved',
			})
			onResolvePending?.()
			onClose()
		} catch (e) {
			console.error(e)
		} finally {
			setActionLoading(false)
		}
	}

	const handleTextSelected = useCallback(
		anchor => {
			if (isEditing) {
				setSelection(null)
				editorRef.current?.clearActiveAnchor?.()
				return
			}
			if (!anchor) {
				setSelection(null)
				editorRef.current?.clearActiveAnchor?.()
				return
			}
			setSelection(anchor)
			setFeedbackOpen(true)
			editorRef.current?.setActiveAnchor?.({
				from: anchor.from,
				to: anchor.to,
			})
		},
		[isEditing],
	)

	const clearSelection = useCallback(() => {
		setSelection(null)
		editorRef.current?.clearActiveAnchor?.()
	}, [])

	const triggerHighlight = useCallback(threadId => {
		if (!threadId) return
		setActiveFeedbackId(threadId)
		setHighlightedFeedbackId(threadId)
		window.setTimeout(() => {
			setHighlightedFeedbackId(prev => (prev === threadId ? null : prev))
		}, 1300)
	}, [])

	const handleSendFeedback = useCallback(
		async text => {
			const trimmed = String(text || '').trim()
			if (
				!trimmed ||
				!item?.essayId ||
				!item?.versionId ||
				actionLoading ||
				feedbackSending
			)
				return
			const currentSelection = selection
			setFeedbackSending(true)
			try {
				await postVersionChatMessage(item.essayId, item.versionId, {
					message_text: trimmed.slice(0, 2000),
					anchor: currentSelection
						? {
								text: currentSelection.text,
								contextBefore: currentSelection.contextBefore,
								contextAfter: currentSelection.contextAfter,
							}
						: undefined,
				})
				await patchEssayVersion(item.essayId, item.versionId, {
					status: 'revision_requested',
				})
				clearSelection()
				if (isApplication) {
					await controlled?.reloadFeedback?.({ silent: true })
				} else {
					await loadFeedback(resolvedVersion, { silent: true })
				}
				onChanged?.()
			} catch (e) {
				console.error(e)
			} finally {
				setFeedbackSending(false)
			}
		},
		[
			actionLoading,
			clearSelection,
			controlled,
			feedbackSending,
			isApplication,
			item?.essayId,
			item?.versionId,
			loadFeedback,
			onChanged,
			resolvedVersion,
			selection,
		],
	)

	useEffect(() => {
		const known = knownFeedbackIdsRef.current
		const pending = pendingFocusFeedbackIdRef.current
		resolvedFeedbackItems.forEach(f => {
			if (f?.id) known.add(f.id)
		})
		if (pending && known.has(pending)) {
			pendingFocusFeedbackIdRef.current = null
			triggerHighlight(pending)
		}
	}, [resolvedFeedbackItems, triggerHighlight])

	const handleAnchorClick = anchor => {
		const thread = resolvedFeedbackItems.find(
			feedback =>
				feedback?.resolvedAnchor?.start === anchor.from &&
				feedback?.resolvedAnchor?.end === anchor.to,
		)
		if (!thread) return
		setFeedbackOpen(true)
		editorRef.current?.setActiveAnchor?.({
			from: anchor.from,
			to: anchor.to,
		})
		triggerHighlight(thread.id)
	}

	if (!item) return null

	const essayThreadTitle = String(item.title || '').trim() || 'Essay'
	const displayTitle = isApplication
		? essayThreadTitle
		: titleDraft || essayThreadTitle

	const contentJson = parseJson(resolvedVersion?.content_json)
	const wordCount = Number(resolvedVersion?.word_count) || 0
	const updatedLabel = formatEssayShortDate(
		item?.updatedAt || resolvedVersion?.updated_at,
	)
	const wordLimit = Number(item?.wordLimit) > 0 ? Number(item.wordLimit) : 600

	const headerActions = (
		<>
			<Button
				size="sm"
				variant="secondary"
				onClick={changeEdit}
				leftIcon={<PenIcon className={s.penIcon} />}
				disabled={actionLoading}
			>
				{isEditing ? 'Done' : 'Edit'}
			</Button>
			<div className={s.feedbackBtnWrap}>
				<Button
					size="sm"
					variant="secondary"
					leftIcon={<MessagesIcon />}
					onClick={() => setFeedbackOpen(v => !v)}
					disabled={actionLoading}
				>
					Feedback
				</Button>
				{resolvedUnread > 0 ? (
					<span className={s.feedbackBadge}>+{resolvedUnread} </span>
				) : null}
			</div>
			<Button
				size="sm"
				onClick={handleApprove}
				leftIcon={<CheckIcon />}
				disabled={actionLoading}
			>
				{actionLoading ? 'Saving...' : 'Approve'}
			</Button>
		</>
	)

	const applicationHeaderExtra = isApplication ? (
		<Button
			size="sm"
			variant="secondary"
			onClick={controlled?.onToggleListPanel}
			leftIcon={<ListIcon />}
			disabled={actionLoading}
		>
			All essays
		</Button>
	) : null

	return (
		<PriorityDrawerShell
			item={item}
			items={items}
			index={index}
			onClose={onClose}
			onNavigate={onNavigate}
			wide={feedbackOpen || (isApplication && controlled?.listPanelOpen)}
			headerActions={headerActions}
			headerLeadingActions={applicationHeaderExtra}
			navigationType={isApplication ? 'essay' : 'simple'}
			showFooter={!isApplication || items.length > 1}
			bodyClassName={[
				s.bodyGrid,
				feedbackOpen ? s.split : '',
				isApplication && controlled?.listPanelOpen ? s.withList : '',
			]
				.filter(Boolean)
				.join(' ')}
		>
			{isApplication && controlled?.listPanelOpen ? (
				<EssayListSidePanel
					open
					items={controlled.listPanelItems}
					activeEssayId={controlled.activeEssayId}
					loading={controlled.listLoading}
					onSelect={controlled.onSelectListEssay}
					onClose={controlled.onToggleListPanel}
				/>
			) : null}
			<EssayContent
				ref={editorRef}
				loading={resolvedLoading}
				pendingUpdate={isApplication ? controlled?.pendingUpdate : null}
				onApplyPendingUpdate={controlled?.onApplyPendingUpdate}
				title={displayTitle}
				isEditing={isEditing}
				onTitleChange={setTitleDraft}
				versionControl={
					isApplication ? (
						<EssayVersionDropdown
							versions={controlled?.versionsMeta || []}
							activeVersionId={controlled?.activeVersionId}
							onSelect={controlled?.onVersionSelect}
							disabled={actionLoading}
						/>
					) : null
				}
				versionNumber={isApplication ? null : item.versionNumber || '1'}
				isNew={Boolean(item?.isNew)}
				updatedLabel={updatedLabel}
				contentLabel="Student Essay"
				wordCount={wordCount}
				wordLimit={wordLimit}
				versionId={item.versionId}
				contentJson={contentJson}
				contentText={resolvedVersion?.content_text || ''}
				onAnchorClick={handleAnchorClick}
				onTextSelected={handleTextSelected}
				selectionDisabled={isEditing}
				saving={saving}
			/>
			<FeedbackPanel
				essayId={item.essayId}
				versionId={item.versionId}
				open={feedbackOpen}
				items={resolvedFeedbackItems}
				loading={resolvedFeedbackLoading}
				activeFeedbackId={activeFeedbackId}
				highlightedFeedbackId={highlightedFeedbackId}
				selection={selection}
				onClearSelection={clearSelection}
				onSend={handleSendFeedback}
				sendDisabled={actionLoading || feedbackSending}
				onClose={() => setFeedbackOpen(false)}
				onReload={() =>
					isApplication ? controlled?.reloadFeedback?.() : loadFeedback(version)
				}
			/>
		</PriorityDrawerShell>
	)
}

export default EssayReviewDrawer
